<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            @rowSelected="preview"
            download-filename="Wideband Orders"
            ajax-path="wideband/orders"
            :ajax-params="ajaxParams"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            :refresh.sync="refreshTable"
        />
        <app-modal 
            width="lg:w-2/3"
            bg="bg-gray-200"
            :show.sync="showPreviewModal"
            @close="showPreviewModal = false"
        >
            <template slot="header">ORDER DETAILS <small class="text-gray-600 uppercase">(STATUS: {{ order.status }})</small></template>
            <template slot="body">
                <div class="card">
                    <table class="table">
                        <tr>
                            <td><strong>Wideband Order ID</strong></td>
                            <td>
                                <div>{{ order.id }}</div>
                            </td>
                            <td><strong>Your Reference</strong></td>
                            <td>
                                <div class="break-all whitespace-normal">{{ order.access_seeker_ref || 'N/A' }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Created At</strong></td>
                            <td>
                                <div>{{ order.created_at_for_human }}</div>
                            </td>
                            <td><strong>Updated At</strong></td>
                            <td>
                                <div>{{ order.updated_at_for_human }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Access Technology</strong></td>
                            <td>
                                <div>{{ order.nbn_access_technology }}</div>
                            </td>
                            <td><strong>Order Type</strong></td>
                            <td>
                                <div>{{ order.order_type }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Address</strong></td>
                            <td colspan="3">
                                <div>{{ order.formatted_address }}<br>
                                <small class="text-gray-500">NBN Location ID: {{ order.nbn_location_id }}</small></div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>NBN Order ID</strong></td>
                            <td>
                                <div>{{ order.nbn_order_id }}</div>
                            </td>
                            <td v-if="order.nbn_pri_id"><strong>NBN PRI ID</strong></td>
                            <td v-if="order.nbn_pri_id">
                                <div>{{ order.nbn_pri_id }}</div>
                            </td>
                        </tr>
                        <tr v-if="order.nbn_avc_id">
                            <td><strong>NBN AVC ID</strong></td>
                            <td colspan="3">
                                <div>{{ order.nbn_avc_id }}</div>
                            </td>
                        </tr>
                        <tr v-if="order.appointment">
                            <td><strong>Appointment ID</strong></td>
                            <td>
                                <div>{{ order.appointment_id }}</div>
                            </td>
                            <td><strong>Appointment Status</strong></td>
                            <td>
                                <div class="whitespace-normal">
                                    {{ order.appointment.status }} 
                                    <span v-if="order.appointment.reason_code">({{ order.appointment.reason_code }})</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="order.appointment">
                            <td width="210"><strong>Appointment Timeframe</strong></td>
                            <td colspan="3">
                                <div>{{ order.appointment.start_time_local }} - {{ order.appointment.end_time_local }}</div>
                            </td>
                        </tr>
                    </table>                    
                </div>
                <h2 class="font-thin mt-10 mb-6 tracking-widest">ORDER UPDATES</h2>
                <div class="card">
                    <table class="table">
                        <thead>
                            <tr>
                                <th width="200">CREATED</th>
                                <th>NOTIFICATION TYPE</th>
                            </tr>
                        </thead>
                        <tbody>                            
                            <tr v-for="update in order.updates">
                                <td>{{ update.created_at_for_human }}</td>
                                <td>
                                    <div>{{ update.notification_type }}</div>
                                    <div 
                                        class="text-xs text-gray-600 whitespace-normal"
                                        v-if="update.update_data && update.update_data.ProductOrder && update.update_data.ProductOrder.Note && update.update_data.ProductOrder.Note.DescribedBy"
                                    >
                                        Reason: {{ update.update_data.ProductOrder.Note.description }}<br>
                                        {{ update.update_data.ProductOrder.Note.DescribedBy.value }}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </app-modal>        
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';    

    export default {
        name: 'widebandClosedOrders',
        data() {
            return {
                showPreviewModal: false,
                ajaxParams: {type: 'closed'},
                tableIsLoading: false,
                tableLoadingText: null,
                refreshTable: false,                        
                columns: [
                    {
                        title: 'ID',
                        field: 'id',
                        sorter: 'string',
                        minWidth: 90,
                    },
                    {
                        title: 'Reference',
                        field: 'access_seeker_ref',
                        sorter: 'string',
                        minWidth: 220,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return 'N/A';
                        }
                    },
                    {
                        title: 'Created',
                        field: 'created_at_for_human',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Last Notification',
                        field: 'last_notification_type',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Appoinment Timeframe',
                        field: 'appointment',
                        sorter: 'string',
                        minWidth: 350,
                        variableHeight: true,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return `${_cell.value.start_time_local} - ${_cell.value.end_time_local}`;
                            }

                            return 'N/A';
                        }
                    },
                    {
                        title: 'Appointment Status',
                        field: 'appointment',
                        sorter: 'string',
                        formatter({_cell}) {
                            if (_cell.value && _cell.value) {
                                return _cell.value.status;
                            }

                            return 'N/A';
                        },
                        minWidth: 200,
                    },
                    {
                        title: 'NBN Order ID',
                        variableHeight: true,
                        field: 'nbn_order_id',
                        minWidth: 180,
                    },
                    {
                        title: 'NBN Location ID',
                        variableHeight: true,
                        field: 'nbn_location_id',
                        minWidth: 200,
                    },
                    {
                        title: 'NBN AVC ID',
                        variableHeight: true,
                        field: 'nbn_avc_id',
                        minWidth: 180,
                    },
                    {
                        title: 'Last Updated',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 200,
                    }
                ],
                actions: [
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                orders: 'wideband/orders/items',
                order: 'wideband/orders/selected',
            })
        },
        methods: {            
            preview(row) {
                this.showPreviewModal = true;
                this.$store.commit('wideband/orders/setSelected', row.data);
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('wideband/orders/sync', 'closed')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = false;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            }
        }
    }
</script>